// Какой экран показать на /cabinet/edu — решение, вынесенное из компонента.
//
// ЧТО БЫЛО. Завершённая анкета возвращала терминальную карточку «Готово. Мы
// посчитаем варианты и покажем отчёт — это займёт меньше минуты», и на этом
// всё: ни перехода, ни повторного запроса, ни ссылки. Отчёт при этом уже был
// посчитан и лежал в базе. Семья, дошедшая до конца, упиралась в обещание и
// не имела ни одного способа увидеть результат — перезагрузка возвращала ту
// же карточку.
//
// ПОЧЕМУ ОТДЕЛЬНЫМ МОДУЛЕМ. Здесь четыре состояния и переходы между ними, а
// внутри компонента они были размазаны по трём флагам и одному `if`. Чистая
// функция позволяет проверить переходы прогоном, а не «поправил и на глаз».
//
// ОДНО ПРАВИЛО, КОТОРОЕ ЗДЕСЬ ГЛАВНОЕ: ожидание конечно. Экран «меньше
// минуты» имеет право существовать ровно пока расчёт идёт; если он не
// закончился за отведённое число попыток, это не «ещё чуть-чуть», а отказ, и
// сказать об этом надо словами.

export type EduScreen =
  /** Анкета не завершена — показываем вопросы. */
  | { kind: "form" }
  /**
   * Анкета проходится ЗАНОВО поверх уже выданного отчёта.
   *
   * Отдельное состояние, а не флаг у form: экран обязан сказать вслух, что
   * прежний отчёт остаётся на месте до появления нового. Флагом это решение
   * было бы принято молча — а «мои ответы стёрли» и «отчёт пропал» это ровно
   * тот страх, из-за которого кнопку и не нажимают.
   */
  | { kind: "retake" }
  /** Отчёт есть. Компонент обязан увести на него, а не рисовать заглушку. */
  | { kind: "report" }
  /** Анкета завершена, отчёта ещё нет — расчёт идёт. */
  | { kind: "computing" }
  /** Расчёт не удался или не закончился за отведённое время. */
  | { kind: "failed"; reason: "error" | "timeout" };

export type EduFlowInput = {
  profileCompleted: boolean;
  reportExists: boolean;
  /** Запрос генерации вернул ошибку. */
  generateFailed: boolean;
  /** Сколько раз уже спросили про отчёт после завершения анкеты. */
  attempts: number;
  /**
   * Семья нажала «пройти анкету заново» и ещё не отправила её.
   *
   * Перебивает «отчёт есть» — иначе кнопка ведёт на экран, который тут же
   * уводит обратно на отчёт: петля, а не переход. Снимается отправкой анкеты,
   * и после этого экран возвращается на общую дорогу: computing → report.
   *
   * ВАЖНО про reportExists при повторном проходе: считать надо появление
   * НОВОГО отчёта, а не наличие какого-нибудь. Старый лежит и никуда не
   * денется, так что «отчёт есть» истинно с первой секунды, и без этой
   * оговорки семья после отправки увидела бы прежний документ вместо расчёта
   * по новым ответам. Кто именно новый — решает маршрут, сравнивая version с
   * той, что была на входе в повторный проход.
   */
  again: boolean;
};

/**
 * Сколько раз опрашиваем отчёт, прежде чем признать, что он не появился.
 *
 * Расчёт идёт секунды: справочник маленький, движок — чистые функции без
 * внешних запросов. Десять попыток по три секунды — тридцать секунд, вдвое
 * больше обещанной «минуты» пополам. Ждать дольше нечестно: если за это время
 * отчёта нет, дело не в медленности.
 */
export const REPORT_POLL_ATTEMPTS = 10;
export const REPORT_POLL_MS = 3000;

export function eduScreen(input: EduFlowInput): EduScreen {
  // Просьба пройти заново сильнее всего остального: она и высказана позже
  // всего остального. Стоит первой намеренно — под правилом «отчёт есть»
  // повторный проход был бы недостижим в принципе.
  if (input.again) return { kind: "retake" };
  // Отчёт есть — он и есть ответ, независимо от статуса анкеты. Анкету могли
  // не дозаполнить после пересчёта, а документ уже выдан.
  if (input.reportExists) return { kind: "report" };
  if (!input.profileCompleted) return { kind: "form" };
  if (input.generateFailed) return { kind: "failed", reason: "error" };
  if (input.attempts >= REPORT_POLL_ATTEMPTS) return { kind: "failed", reason: "timeout" };
  return { kind: "computing" };
}

/**
 * «Пройти заново» из строки запроса.
 *
 * ВЫНЕСЕНО ИЗ МАРШРУТА РАДИ ПРОВЕРЯЕМОСТИ, и повод конкретный: полгода
 * незаметно не работавшая кнопка.
 *
 * Роутер разбирает строку запроса по-JSON'овски, поэтому `?again=1` приезжает
 * в валидатор ЧИСЛОМ 1, а не строкой «1». Сравнение со строкой давало false,
 * параметр объявлялся лишним, и роутер отвечал 307-м редиректом на тот же
 * адрес без него — после чего экран честно уводил на отчёт, потому что
 * повторного прохода в адресе больше не было.
 *
 * Здесь принимается ЛЮБОЕ представление единицы, потому что нам всё равно, как
 * её разобрал транспорт, и НЕ принимается ничего другого: `true` — не «заново».
 */
export function retakeFromSearch(value: unknown): "1" | undefined {
  return String(value) === "1" ? "1" : undefined;
}
